import { FirebaseAuthTypes } from '@react-native-firebase/auth';
import { ConfirmationResult } from 'firebase/auth';
import React, { JSX } from 'react';

import { SignWithEmailResponse } from '@/api/models/authModels';
import { Me, NonProfitOnBoardingPreference, Preference, User } from '@/api/models/meModels';

interface SignUpData {
  confirmation?: FirebaseAuthTypes.ConfirmationResult | ConfirmationResult;
  loginEmail?: SignWithEmailResponse;
}

interface State {
  userId?: string;
  signUpData?: Partial<SignUpData>;
  phone?: string;
  providerId?: string;
  email?: string;
  user?: Me;
  preferenceId?: string;
  authReady: boolean;
  isBuyingShow?: boolean;
}

interface BuyingShow {
  type: 'BUYING_SHOW';
  isBuyingShow?: boolean;
}

interface AuthReadyAction {
  type: 'AUTH_READY';
}

interface SignInAction {
  type: 'SIGN_IN';
  userId: string;
  phone: string;
  providerId: string;
  user?: Me;
}

interface NpoSignInAction {
  type: 'NPO_SIGN_IN';
  userId: string;
  email: string;
  providerId: string;
  user?: Me;
}

interface SetSignUpDataAction {
  type: 'SET_SIGN_UP_DATA';
  signUpData: Partial<SignUpData>;
  preferenceId: string;
}

interface ClearSignUpDataAction {
  type: 'CLEAR_SIGN_UP_DATA';
}

interface SignOutDataAction {
  type: 'SIGN_OUT';
}

interface SetPreferencesAction {
  type: 'SET_PREFERENCES';
  programTypes: Preference[];
  denominationTypes: Preference[];
  religionTypes: Preference[];
}

interface SetNpoSubscribed {
  type: 'SET_NPO_SUBSCRIBED';
  subscribedToDonationChatRooms: any;
}

interface SetChurchPreferencesAction {
  type: 'SET_CHURCH_PREFERENCES';
  programTypes: Preference[];
  denominationTypes: Preference[];
  religionTypes: Preference[];
}

interface SetSecularPreferencesAction {
  type: 'SET_SECULAR_PREFERENCES';
  programTypes: Preference[];
}

interface SetMeResponseAction {
  type: 'SET_ME_RESPONSE';
  meResponse: Me;
}

interface SetCurrentUser {
  type: 'SET_CURRENT_USER';
  user: User;
}

interface ClearCurrentUser {
  type: 'CLEAR_CURRENT_USER';
}

type Action =
  | BuyingShow
  | AuthReadyAction
  | SignOutDataAction
  | SignInAction
  | NpoSignInAction
  | SetSignUpDataAction
  | ClearSignUpDataAction
  | SetPreferencesAction
  | SetNpoSubscribed
  | SetChurchPreferencesAction
  | SetSecularPreferencesAction
  | SetMeResponseAction
  | SetCurrentUser
  | ClearCurrentUser;
type Dispatch = (action: Action) => void;
interface PreferenceProps {
  programTypes: Preference[];
  denominationTypes?: Preference[];
  religionTypes?: Preference[];
}

interface NpoSubscribedProps {
  subscribedToDonationChatRooms: any;
}

interface AuthContextData {
  state: State;
  dispatch: Dispatch;
  setPreferences: (data: PreferenceProps) => void;
  setNpoSubscribed: (data: NpoSubscribedProps) => void;
  setSecularPreferences: (data: PreferenceProps) => void;
  setChurchPreferences: (data: PreferenceProps) => void;
  setMeResponse: (data: Me) => void;
  setCurrentUser: (data: User) => void;
  clearCurrentUser: () => void;
  isBrowseOnlyUser?: boolean;
}

const initState: State = {
  userId: undefined,
  phone: undefined,
  signUpData: undefined,
  user: undefined,
  providerId: undefined,
  email: undefined,
  preferenceId: undefined,
  authReady: false,
  isBuyingShow: false
};

const AuthContext = React.createContext<AuthContextData | undefined>(undefined);

function authReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'BUYING_SHOW':
      return {
        ...state,
        isBuyingShow: action.isBuyingShow
      };
    case 'AUTH_READY':
      return {
        ...state,
        authReady: true
      };
    case 'SIGN_IN':
      return {
        ...state,
        userId: action.userId,
        phone: action.phone,
        providerId: action.providerId,
        user: action.user
      };
    case 'NPO_SIGN_IN':
      return {
        ...state,
        userId: action.userId,
        email: action.email,
        providerId: action.providerId,
        user: action.user
      };
    case 'SIGN_OUT':
      return {
        ...initState,
        authReady: state.authReady
      };
    case 'SET_SIGN_UP_DATA':
      return {
        ...state,
        signUpData: { ...state.signUpData, ...action.signUpData },
        preferenceId: action.preferenceId
      };
    case 'CLEAR_SIGN_UP_DATA':
      return {
        ...state,
        signUpData: undefined
      };
    case 'SET_PREFERENCES':
      return {
        ...state,
        user: {
          ...state.user!,
          onBoardingPreference: {
            ...state.user?.onBoardingPreference!,
            programTypes: action.programTypes,
            denominationTypes: action.denominationTypes,
            religionTypes: action.religionTypes
          }
        }
      };
    case 'SET_NPO_SUBSCRIBED':
      return {
        ...state,
        user: {
          ...state.user!,
          onBoardingPreference: {
            ...state.user?.onBoardingPreference!,
            subscribedToDonationChatRooms: action.subscribedToDonationChatRooms
          }
        }
      };
    case 'SET_SECULAR_PREFERENCES':
      const updatedPreferencesSecular: NonProfitOnBoardingPreference = {
        ...state.user?.onBoardingPreferenceSecular?.[0]!,
        programTypes: action.programTypes
      };

      return {
        ...state,
        user: {
          ...state.user!,
          onBoardingPreferenceSecular: [updatedPreferencesSecular]
        }
      };
    case 'SET_CHURCH_PREFERENCES':
      const updatedPreferencesChurch: NonProfitOnBoardingPreference = {
        ...state.user?.onBoardingPreferenceChurch?.[0]!,
        religionTypes: action.religionTypes,
        denominationTypes: action.denominationTypes,
        programTypes: action.programTypes
      };

      return {
        ...state,
        user: {
          ...state.user!,
          onBoardingPreferenceChurch: [updatedPreferencesChurch]
        }
      };

    case 'SET_ME_RESPONSE':
      return {
        ...state,
        user: action.meResponse
      };

    case 'SET_CURRENT_USER':
      return {
        ...state,
        user: {
          user: action.user,
          onBoardingPreference: state.user?.onBoardingPreference ?? null,
          onBoardingPreferenceSecular: state.user?.onBoardingPreferenceSecular ?? null,
          onBoardingPreferenceChurch: state.user?.onBoardingPreferenceChurch ?? null
        }
      };

    case 'CLEAR_CURRENT_USER':
      return {
        ...state,
        user: undefined
      };
    default:
      throw new Error(`Unhandled action type`);
  }
}

function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(authReducer, initState);

  const setPreferences = (data: PreferenceProps) => {
    dispatch({
      type: 'SET_PREFERENCES',
      programTypes: data.programTypes,
      religionTypes: data.religionTypes ?? [],
      denominationTypes: data.denominationTypes ?? []
    });
  };

  const setNpoSubscribed = (data: NpoSubscribedProps) => {
    dispatch({
      type: 'SET_NPO_SUBSCRIBED',
      subscribedToDonationChatRooms: data.subscribedToDonationChatRooms
    });
  };

  const setSecularPreferences = (data: PreferenceProps) => {
    dispatch({
      type: 'SET_SECULAR_PREFERENCES',
      programTypes: data.programTypes
    });
  };

  const setChurchPreferences = (data: PreferenceProps) => {
    dispatch({
      type: 'SET_CHURCH_PREFERENCES',
      religionTypes: data.religionTypes ?? [],
      programTypes: data.programTypes,
      denominationTypes: data.denominationTypes ?? []
    });
  };

  const setMeResponse = (data: Me) => {
    dispatch({
      type: 'SET_ME_RESPONSE',
      meResponse: data
    });
  };

  const setCurrentUser = (data: User) => {
    dispatch({
      type: 'SET_CURRENT_USER',
      user: data
    });
  };

  const clearCurrentUser = () => {
    dispatch({
      type: 'CLEAR_CURRENT_USER'
    });
  };

  const isBrowseOnlyUser = state.user?.user?.role !== 'INDIVIDUAL' && state.user?.user?.role !== 'NON_PROFIT_ORG';

  return (
    <AuthContext.Provider
      value={{
        state,
        dispatch,
        setPreferences,
        setNpoSubscribed,
        setSecularPreferences,
        setChurchPreferences,
        setMeResponse,
        setCurrentUser,
        clearCurrentUser,
        isBrowseOnlyUser
      }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth(): AuthContextData {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}

export { AuthProvider, useAuth };
